import type { WinMeterView } from "./WinMeterView";
import type { WinMeterTipsTuning } from "../../tuning/winMeterTipsTuning";
import type { PresentationTaskRunner } from "@slot/presentation";
import type { MoneyContext, SpinPhaseStore } from "@slot/game-shell";

type ScopeId = string;

/**
 * The plank above the grid: the spin's running total when there is one, else — while the game is idle — one-line
 * tips taking turns (`WinMeterTipsTuning`). A round holds the tip where it is; a total about to land clears it
 * (`clearTip`); the tips come back at the next idle that leaves the plank without a total.
 */
export class WinMeterPresenter {
  private readonly panel: WinMeterView;
  private readonly runner: PresentationTaskRunner;
  private readonly chromeRunner: PresentationTaskRunner;
  private readonly moneyContext: MoneyContext;
  private readonly tips: WinMeterTipsTuning;

  private isVisible = false;
  /** The tip up, or the last one: the turns go on from it. */
  private tipIndex = -1;
  private tipShown = false;
  /** Bumped by every phase change and clear: a rotation started before it stops. */
  private tipRun = 0;

  private get fmtOptions(): { omitSymbol?: boolean } | undefined {
    return this.panel.amountOmitsCurrencySymbol ? { omitSymbol: true } : undefined;
  }

  /** Last presented TOTAL in engine MINOR units (integer string). */
  private lastTotalWinMinor: string = "0";

   // Feel knobs live in ONE place
   private readonly countAnim = {
    minSeconds: 0.18,
    maxSeconds: 0.45,
    perDigitSeconds: 0.03,   // larger jumps animate longer
    pulseAtEnd: true,
    endPulseScale: 1.12,
    endPulseSeconds: 0.18,
    easing: "quadOut" as const,
  };

  public constructor(input: {
    panel: WinMeterView;
    moneyContext: MoneyContext;
    runner: PresentationTaskRunner;
    /** The tips' turns and fades: real seconds, frozen by the dev pause. */
    chromeRunner: PresentationTaskRunner;
    /** Whether the game is idle: the tips take turns only then. */
    spinPhase: SpinPhaseStore;
    tips: WinMeterTipsTuning;
  }) {
    this.panel = input.panel;
    this.runner = input.runner;
    this.chromeRunner = input.chromeRunner;
    this.moneyContext = input.moneyContext;
    this.tips = input.tips;

    this.hideForNewSpin();
    input.spinPhase.subscribe((phase) => this.onSpinPhase(phase === "idle"));
    this.onSpinPhase(input.spinPhase.getPhase() === "idle");
  }

  // ---------------------------------------------------------------------------
  // Totals (authoritative replay values)
  // ---------------------------------------------------------------------------


  /**
   * Global: the ONLY way to update the displayed TOTAL number. The spin's first total appears as it is (it is
   * the amount that just flew in); a later one counts up from the last, paced under `scopeId`.
   */
  public async presentTotalWinMinor(targetMinor: string, scopeId?: ScopeId): Promise<void> {
    const start = BigInt(this.lastTotalWinMinor);
    const end = BigInt(targetMinor);

    if (end <= 0n) {
      this.lastTotalWinMinor = targetMinor;
      // don't show panel for 0
      return;
    }

    // If same, just ensure formatted text is correct
    if (end === start) {
      this.lastTotalWinMinor = targetMinor;
      this.panel.setText(this.moneyContext.formatter.formatMinor(targetMinor, this.fmtOptions));
      return;
    }

    // The spin's first total appears as it is: it is the amount that just flew in.
    if (!this.isVisible) {
      this.clearTip();
      this.showIfNeeded();
      this.commitTotal(targetMinor);
      return;
    }

    const duration = this.computeDurationSeconds(start, end);

    // Drive number animation with runner (time-scale + slam-safe)
    const diff = end - start;
    const denom = 1000n;

    await this.runner.tweenProgress(
      duration,
      (t01) => {
        const t = Math.max(0, Math.min(1, t01));
        const tn = BigInt(Math.round(t * 1000));
        const cur = start + (diff * tn) / denom;
        this.panel.setText(this.moneyContext.formatter.formatMinor(cur.toString(), this.fmtOptions));
      },
      scopeId,
      { easing: this.countAnim.easing }
    );

    this.commitTotal(targetMinor);
  }

  /** The exact total on the meter, then the end pulse. */
  private commitTotal(targetMinor: string): void {
    this.lastTotalWinMinor = targetMinor;
    this.panel.setText(this.moneyContext.formatter.formatMinor(targetMinor, this.fmtOptions));

    if (this.countAnim.pulseAtEnd) {
      void this.panel.pulse(this.countAnim.endPulseSeconds, this.countAnim.endPulseScale);
    }
  }

  // ---- feel logic centralized here ----
  private computeDurationSeconds(start: bigint, end: bigint): number {
    // Duration based on magnitude of change (rough heuristic)
    const delta = end >= start ? end - start : start - end;
    const digits = delta === 0n ? 1 : delta.toString().length;

    const raw = this.countAnim.minSeconds + digits * this.countAnim.perDigitSeconds;
    return Math.max(this.countAnim.minSeconds, Math.min(this.countAnim.maxSeconds, raw));
  }

  public hideForNewSpin(): void {
    this.isVisible = false;
    this.lastTotalWinMinor = "0";
    this.panel.setText("");
    this.panel.hide();
  }

  public showIfNeeded(): void {
    if (this.isVisible) return;
    this.isVisible = true;
    this.panel.show();
  }

  // ---------------------------------------------------------------------------
  // Tips (the plank's face while it has no total)
  // ---------------------------------------------------------------------------

  /** A total is about to land on the plank (a win flying in): the tip makes way. */
  public clearTip(): void {
    this.tipRun += 1;
    if (!this.tipShown) return;
    this.tipShown = false;
    void this.panel.hideTip(this.tips.clearSeconds);
  }

  /** A round holds the tip where it is; the idle starts the turns again. */
  private onSpinPhase(idle: boolean): void {
    this.tipRun += 1;
    if (idle) void this.rotateTips(this.tipRun);
  }

  /** While the game stays idle and the plank has no total: a beat (or the rest of the held tip's turn), then one tip after another. */
  private async rotateTips(run: number): Promise<void> {
    const t = this.tips;
    if (t.keys.length === 0) return;
    await this.chromeRunner.sleep(this.tipShown ? t.showSeconds : t.idleDelaySeconds);
    while (run === this.tipRun && !this.isVisible) {
      this.tipIndex = (this.tipIndex + 1) % t.keys.length;
      this.tipShown = true;
      await this.panel.showTip(t.keys[this.tipIndex]!, t.fadeSeconds);
      if (run !== this.tipRun) return;
      await this.chromeRunner.sleep(t.showSeconds);
    }
  }
}
